import {
    Positionable,
    SimpleReelModel,
    SimpleReelModelDelegate,
    SimpleReelModelState,
    SimpleReelModelSymbolData,
    SimpleReelModelSymbolPosition
} from "../../core/reel/simplereel/SimpleReelModel";
import {gsap} from "gsap";
import {LayersSet} from "./LayersSet";
import {ReelSet} from "./ReelSet";
import {XwaysHoarder2Statics} from "../XwaysHoarder2Statics";
import {GameMode} from "@nolimitcity/slot-game/bin/flowcontrol/GameMode";
import {BasicSymbol, SymbolStates} from "./symbols/BasicSymbol";
import {XSplitSymbol} from "./symbols/XSplitSymbol";
import {XWaySymbol} from "./symbols/XWaySymbol";
import {LTSymbol} from "./symbols/LTSymbol";
import {ScatterSymbol} from "./symbols/ScatterSymbol";
import {DummySymbol} from "./symbols/DummySymbol";
import {XNudgeSymbol} from "./symbols/XNudgeSymbol";
import {NormalSymbol} from "./symbols/NormalSymbol";
import {XwaysHoarder2} from "../XwaysHoarder2";

export interface SymbolReplaceData {
    oldSymbolData: SimpleReelModelSymbolData;
    newSymbolData: SimpleReelModelSymbolData;
}

export class Reel implements SimpleReelModelDelegate {
    public isScaled: boolean = false;
    public id: number;
    public model: SimpleReelModel;
    public layers: LayersSet;


    public position: PIXI.ObservablePoint;
    public pivot: PIXI.ObservablePoint;
    public scale: PIXI.ObservablePoint;
    public isVisuallyLocked: boolean = false;
    public xnLandSoundPlayed: boolean = false;
    private currentReelSet: string[][];
    private spinAnimation: gsap.core.Timeline;

    constructor(id: number) {


        this.position = new PIXI.ObservablePoint(this.onPosition, this);
        this.pivot = new PIXI.ObservablePoint(this.onPivot, this);
        this.scale = new PIXI.ObservablePoint(this.onScale, this);

        this.id = id;

        this.model = new SimpleReelModel(id, this, true);
        this.model.setReelSet(ReelSet.DEFAULT_REEL_SET[id]);
        this.model.stepDuration = XwaysHoarder2Statics.stepDuration;
        this.model.stepSize = XwaysHoarder2Statics.symbolHeight;

        // On events

        // Layers
        this.layers = new LayersSet(`reel_${this.id}_`);
        //const hack:any = NolimitApplication.pixiApp.renderer.mask;
        //hack.enableScissor = true;

    }

    public init(reelData: string[], isFreespin: boolean = false) {
        const reelArray: string[] = JSON.parse(JSON.stringify(reelData));
        reelArray.forEach((symName: string, index: number) => {
            if (symName == "W") {
                reelArray[index] = "XS";
            }
        })
        this.model.init(reelArray);

    }

    public start() {
        const tl: gsap.core.Timeline = gsap.timeline();
        tl.add(gsap.to(this.layers.maskedNormal, {duration: 1, alpha: 0.8, ease: "power3.out"}));
        this.spinAnimation = tl;
    }

    public stop() {
        this.spinAnimation.kill();
        gsap.to(this.layers.maskedNormal, {duration: 0.1, alpha: 1});
    }

    public changeReelSet(nextMode: GameMode, nextLevel: number) {
        let reelSet = ReelSet.DEFAULT_REEL_SET;

        if (nextMode === "NORMAL") {
            reelSet = ReelSet.DEFAULT_REEL_SET;
        } else if (nextMode === "RESPIN") {
            reelSet = ReelSet.DEFAULT_REEL_SET;
        } else if (nextMode === "FREESPIN" || nextMode === "SUPER_FREESPIN") {
            if (nextLevel === 0) {
                reelSet = ReelSet.BONUS_GAME_LEVEL_ZERO_REEL_SET;
            } else if (nextLevel === 1) {
                reelSet = ReelSet.BONUS_GAME_LEVEL_ONE_REEL_SET;
            } else if (nextLevel === 2) {
                reelSet = ReelSet.BONUS_GAME_LEVEL_TWO_REEL_SET;
            } else if (nextLevel === 3) {
                reelSet = ReelSet.BONUS_GAME_LEVEL_THREE_REEL_SET;
            } else if (nextLevel === 4) {
                reelSet = ReelSet.BONUS_GAME_LEVEL_FOUR_REEL_SET;
            }
        }

        if (reelSet == this.currentReelSet) {
            return
        }
        this.currentReelSet = reelSet;
        this.model.setReelSet(reelSet[this.id])
    }

    createSymbol(symbolName: string, state: SimpleReelModelState, symbolPosition: SimpleReelModelSymbolPosition, symbolSize: number): Positionable {
        const symbolState: SymbolStates = state === SimpleReelModelState.REMOVED ? SymbolStates.REMOVED : (state == SimpleReelModelState.SPIN || state == SimpleReelModelState.STOP || state == SimpleReelModelState.START) ? SymbolStates.SPIN : SymbolStates.NORMAL;
        let sym: BasicSymbol;

        if (symbolName == "XS" || symbolName == "W") {
            symbolName = "XS";
            sym = new XSplitSymbol(symbolName, symbolState, this, symbolPosition, symbolSize);
        } else if (symbolName == "XW") {
            sym = new XWaySymbol(symbolName, symbolState, this, symbolPosition);
        } else if (symbolName == "SC" || symbolName == "SSC") {
            sym = new ScatterSymbol(symbolName, symbolState, this, symbolPosition);
        } else if (symbolName == "LT" || symbolName == "LTX") {
            sym = new LTSymbol(symbolName, symbolState, this, symbolPosition);
        } else if (symbolName == "D") {
            sym = new DummySymbol(symbolName, symbolState, this, symbolPosition);
        } else if (symbolName == "XN") {
            sym = new XNudgeSymbol(symbolName, symbolState, this, symbolPosition);
        } else {
            sym = new NormalSymbol(symbolName, symbolState, this, symbolPosition)
        }
        return sym;
    }

    public replaceSymbolOutsideReel(position: number, newSymbolName: string, updatePosition: boolean = true): SymbolReplaceData {
        const visibleIndex = this.model.visibleIndexToRealIndex(position);

        const newSymbol: SimpleReelModelSymbolData = {
            name: newSymbolName,
            positionable: this.model._delegate.createSymbol(newSymbolName, SimpleReelModelState.IDLE, {
                reelID: this.model.id,
                position: position
            }, 1),
            reelPosition: position,
            reelId: this.model.id,
            symbolSize: 1
        }

        const oldSymbol = this.model.symbols.splice(visibleIndex, 1, newSymbol)[0];

        if (updatePosition) {
            this.model.forcePositionUpdate();
        }

        (<BasicSymbol>oldSymbol.positionable).hide();
        this.removeSymbol(oldSymbol);

        return {oldSymbolData: oldSymbol, newSymbolData: newSymbol}
    }

    public lockSpecialSymAndAddDummyBelow(position: number, newSymbolName: string, updatePosition: boolean = true): SymbolReplaceData {
        const visibleIndex = this.model.visibleIndexToRealIndex(position);

        const newSymbol: SimpleReelModelSymbolData = {
            name: newSymbolName,
            positionable: this.model._delegate.createSymbol(newSymbolName, SimpleReelModelState.IDLE, {
                reelID: this.model.id,
                position: position
            }, 1),
            reelPosition: position,
            reelId: this.model.id,
            symbolSize: 1
        }

        const oldSymbol = this.model.symbols.splice(visibleIndex, 1, newSymbol)[0];

        if (updatePosition) {
            this.model.forcePositionUpdate();
        }
        if ((<BasicSymbol>oldSymbol.positionable).symbolName === "XW") {
            this.model.xwaysArray.push(oldSymbol);
        } else if ((<BasicSymbol>oldSymbol.positionable).symbolName === "XS") {
            this.model.xsplitArray.push(oldSymbol);
        } else if ((<BasicSymbol>oldSymbol.positionable).symbolName === "SC" || (<BasicSymbol>oldSymbol.positionable).symbolName === "SSC") {
            this.model.scatterArray.push(oldSymbol);
        } else if ((<BasicSymbol>oldSymbol.positionable).symbolName === "LT") {
            this.model.LTarray.push(oldSymbol);
            oldSymbol.reelPosition = 0;
        }
        return {oldSymbolData: oldSymbol, newSymbolData: newSymbol}
    }


    public replaceSymOnReel(position: number, newSymbolName: string, updatePosition: boolean = true, symbolState?: SymbolStates, textureName?: string, currentSize?: number): gsap.core.Timeline {
        const actualIndex = this.model.visibleIndexToRealIndex(position);
        let specialCase: boolean = false;
        if (this.model.symbols[actualIndex].name === "XN" && newSymbolName === "XS" && !(XwaysHoarder2.model.data.mode==="RESPIN" && XwaysHoarder2.model.data.nextMode==="RESPIN")) {
            specialCase = true;
        }


        const newSymbol: SimpleReelModelSymbolData = {
            name: newSymbolName,
            positionable: this.model._delegate.createSymbol(newSymbolName, specialCase ? SimpleReelModelState.REMOVED : SimpleReelModelState.IDLE, {
                reelID: this.model.id,
                position: position,
            }, currentSize ? currentSize : 1),
            reelPosition: position,
            reelId: this.model.id,
            symbolSize: currentSize ? currentSize : 1
        }

        const oldSymbol = this.model.symbols.splice(actualIndex, 1, newSymbol)[0];

        (<BasicSymbol>newSymbol.positionable).setSpriteTexture(textureName);
        if (updatePosition) {
            this.model.forcePositionUpdate();
        }
        (<BasicSymbol>oldSymbol.positionable).destroy();
        //console.log(this.model.symbols);
        return gsap.timeline();
    }

    removeSymbol(symbolData: SimpleReelModelSymbolData, state?: SimpleReelModelState): void {
        (<BasicSymbol>symbolData.positionable).destroy();
    }

    public makeLTfirstSymbolOfReel(ltsym: BasicSymbol, symId: number, isRespinStarting: boolean): void {
        if (this.id === 0) {
            const checkIndex: number = this.model.visibleIndexToRealIndex(0);
            const currentLTInddex: number = this.model.visibleIndexToRealIndex(symId);

            if (!isRespinStarting) {
                (<BasicSymbol>this.model.symbols[currentLTInddex].positionable).destroy();
            }
            this.model.symbols.splice(currentLTInddex, 1);
            this.model.symbols.splice(checkIndex, 0, {
                name: "LT",
                positionable: ltsym,
                reelPosition: 0,
                reelId: this.model.id,
                symbolSize: 1
            });
            if (isRespinStarting) {
                this.model.LTarray = [];
                this.model.LTarray.push({
                    name: "LT",
                    positionable: ltsym,
                    reelPosition: 0,
                    reelId: this.model.id,
                    symbolSize: 1
                });
                ltsym.isSticky = true;
                (<LTSymbol>ltsym).setSpinePos(true);
            } else {
                ltsym.position.y = 0
                ltsym.isSticky = false;
                (<LTSymbol>ltsym).setSpinePos(false);
                this.model.LTarray = [];
            }

            // this.model.forcePositionUpdate();


        }
    }

    public stickySymbolsLayerUp(): void {
        this.model.symbols.forEach((symbol: SimpleReelModelSymbolData, index: number) => {
            const mySymbol: BasicSymbol = <BasicSymbol>symbol.positionable;
            if ((mySymbol.symbolName === "SC" || mySymbol.symbolName === "SSC") && mySymbol.isSticky) {
                const scatterData: SimpleReelModelSymbolData = {
                    name: mySymbol.symbolName,
                    positionable: mySymbol,
                    reelPosition: mySymbol.position.y,
                    reelId: this.id,
                    symbolSize: symbol.symbolSize
                }
                mySymbol.moveToStickyLayer();
                this.model.scatterArray.push(scatterData);
            }
        });
    }

    private onPosition() {
        for (let layer of this.layers.all) {
            layer.position.set(this.position.x, this.position.y);
        }
    }

    private onPivot() {
        for (let layer of this.layers.all) {
            layer.pivot.set(this.pivot.x, this.pivot.y);
        }
    }

    private onScale() {
        for (let layer of this.layers.all) {
            layer.scale.set(this.scale.x, this.scale.y);
        }
    }

}